/**
 * Utilitários para validação e formatação de CNPJ
 * Conforme nova legislação brasileira (Nota Técnica 2026.004)
 * 
 * Novo formato: AA.AAA.AAA/AAAA-00
 * - 12 primeiras posições: letras maiúsculas (A-Z) ou números (0-9)
 * - 2 últimas posições: apenas números (dígitos verificadores)
 * 
 * Regex sem pontuação: ^[A-Z0-9]{12}[0-9]{2}$
 */

// Regex para validação do novo formato de CNPJ (alfanumérico)
export const CNPJ_REGEX = /^[A-Z0-9]{12}[0-9]{2}$/;

// Regex para CNPJ formatado: AA.AAA.AAA/AAAA-00
export const CNPJ_FORMATTED_REGEX = /^[A-Z0-9]{2}\.[A-Z0-9]{3}\.[A-Z0-9]{3}\/[A-Z0-9]{4}-[0-9]{2}$/;

/**
 * Formata CNPJ no novo padrão alfanumérico
 * Entrada: ABC12345600199 ou abc12345600199
 * Saída: AB.C12.345/6001-99
 */
export const formatCNPJ = (value) => {
  if (!value) return '';
  
  // Remove caracteres especiais e converte para maiúsculo
  const cleaned = value.replace(/[^A-Za-z0-9]/g, '').toUpperCase();
  
  // Limita a 14 caracteres
  const limited = cleaned.slice(0, 14);
  
  // Aplica a máscara progressivamente
  if (limited.length <= 2) return limited;
  if (limited.length <= 5) return `${limited.slice(0, 2)}.${limited.slice(2)}`;
  if (limited.length <= 8) return `${limited.slice(0, 2)}.${limited.slice(2, 5)}.${limited.slice(5)}`;
  if (limited.length <= 12) return `${limited.slice(0, 2)}.${limited.slice(2, 5)}.${limited.slice(5, 8)}/${limited.slice(8)}`;
  return `${limited.slice(0, 2)}.${limited.slice(2, 5)}.${limited.slice(5, 8)}/${limited.slice(8, 12)}-${limited.slice(12)}`;
};

/**
 * Remove formatação do CNPJ
 * Entrada: AB.C12.345/6001-99
 * Saída: ABC12345600199
 */
export const cleanCNPJ = (value) => {
  if (!value) return '';
  return value.replace(/[^A-Za-z0-9]/g, '').toUpperCase();
};

/**
 * Valida se o CNPJ tem o formato correto (alfanumérico)
 * - 12 primeiros caracteres: letras ou números
 * - 2 últimos caracteres: apenas números
 */
export const isValidCNPJFormat = (value) => {
  const cleaned = cleanCNPJ(value);
  return CNPJ_REGEX.test(cleaned);
};

/**
 * Verifica se a entrada parece ser um CNPJ (está sendo digitado)
 * Aceita tanto o formato antigo (apenas números) quanto o novo (alfanumérico)
 */
export const looksLikeCNPJ = (value) => {
  const cleaned = value.replace(/[.\-/\s]/g, '').toUpperCase();
  // Se tem letras, é definitivamente o novo formato
  if (/[A-Z]/.test(cleaned)) {
    return cleaned.length <= 14 && /^[A-Z0-9]+$/.test(cleaned);
  }
  // Se tem apenas números, pode ser CNPJ antigo
  return /^\d+$/.test(cleaned) && cleaned.length <= 14 && cleaned.length > 11;
};

/**
 * Formata CNPJ para exibição (compatível com ambos os formatos)
 */
export const displayCNPJ = (value) => {
  const cleaned = cleanCNPJ(value);
  if (cleaned.length !== 14) return value;
  return `${cleaned.slice(0, 2)}.${cleaned.slice(2, 5)}.${cleaned.slice(5, 8)}/${cleaned.slice(8, 12)}-${cleaned.slice(12)}`;
};

/**
 * CPF helpers (mantidos para compatibilidade)
 */
export const formatCPF = (value) => {
  const digits = value.replace(/\D/g, '').slice(0, 11);
  if (digits.length <= 3) return digits;
  if (digits.length <= 6) return `${digits.slice(0, 3)}.${digits.slice(3)}`;
  if (digits.length <= 9) return `${digits.slice(0, 3)}.${digits.slice(3, 6)}.${digits.slice(6)}`;
  return `${digits.slice(0, 3)}.${digits.slice(3, 6)}.${digits.slice(6, 9)}-${digits.slice(9)}`;
};

export const cleanCPF = (value) => {
  return value.replace(/\D/g, '');
};

export const looksLikeCPF = (value) => {
  const cleaned = value.replace(/[\s.-]/g, '');
  return /^\d+$/.test(cleaned) && cleaned.length <= 11;
};

export const isValidCPFFormat = (value) => {
  const cleaned = cleanCPF(value);
  return cleaned.length === 11;
};
